/**
 * Presence store - Tracks online/offline status of friends.
 * Updated via REST API on load and WebSocket for real-time changes.
 */
import { create } from 'zustand';
import { api } from '../api/client';

interface PresenceInfo {
  status: 'online' | 'offline';
  last_seen: string | null;
}

interface PresenceState {
  // Map of userId -> presence info
  presence: Record<string, PresenceInfo>;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchPresence: () => Promise<void>;
  updatePresence: (userId: number, status: 'online' | 'offline', lastSeen?: string | null) => void;
  isOnline: (userId: number) => boolean;
  getLastSeen: (userId: number) => string | null;
  clearPresence: () => void;
}

export const usePresenceStore = create<PresenceState>((set, get) => ({
  presence: {},
  isLoading: false,
  error: null,

  fetchPresence: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await api.get<{ success: boolean; data: Record<string, PresenceInfo> }>(
        '/friends/presence'
      );

      if (response.data?.success && response.data?.data) {
        set({ presence: response.data.data, isLoading: false });
      } else {
        set({ error: 'Failed to fetch presence', isLoading: false });
      }
    } catch (error) {
      console.error('Failed to fetch presence:', error);
      set({ error: 'Failed to fetch presence', isLoading: false });
    }
  },

  updatePresence: (userId, status, lastSeen = null) => {
    set((state) => ({
      presence: {
        ...state.presence,
        [String(userId)]: {
          status,
          last_seen: lastSeen,
        },
      },
    }));
  },

  isOnline: (userId) => {
    const { presence } = get();
    return presence[String(userId)]?.status === 'online';
  },

  getLastSeen: (userId) => {
    const { presence } = get();
    return presence[String(userId)]?.last_seen ?? null;
  },

  clearPresence: () => {
    set({ presence: {}, error: null });
  },
}));
